import { useEffect, useState, useRef } from "react";
import { scrollBoxToBottom } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Send, Phone, Mail, Save, Stethoscope, Shield, PhoneCall, MapPin, ExternalLink, RefreshCw, History, MessageSquareOff } from "lucide-react";
import { Skeleton } from "@/components/Skeleton";
import SortableGrid from "@/components/SortableGrid";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { useEditMode } from "@/lib/editMode";

const API = process.env.REACT_APP_BACKEND_URL;
import { MediaVideo } from "@/lib/mediaSrc";

export default function Messages() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const preselectContactId = qs.get("to");
  const prefillBody = qs.get("prefill");
  // Widget layout for the two patient panels at the bottom of this tab.
  const { editing: editLayout } = useEditMode();
  const {
    visibleOrder: panelOrder,
    setOrder: setPanelOrder,
    setSize: sizePanel,
    sizes: panelSizes,
  } = useDashboardLayout("patient_messages_panels", ["gp", "nhs"]);
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);          // messages currently visible in the pane
  const [endedAt, setEndedAt] = useState(null);          // ISO cutoff — set when user taps "End chat"
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [text, setText] = useState("");
  const [gp, setGp] = useState({ gp_name: "", gp_surgery: "", gp_phone: "", gp_email: "", nhs_number: "" });
  const [gpEdit, setGpEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [unread, setUnread] = useState({});
  const loadUnread = async () => {
    try { const r = await api.get("/messages/unread-summary"); setUnread(r?.data?.per_sender || {}); }
    catch { /* non-fatal */ }
  };
  const bottom = useRef(null);
  const scrollBox = useRef(null);
  const lastCount = useRef(0);
  const shouldStickBottom = useRef(true);      // becomes false when user scrolls up
  const isPatient = user?.role === "patient";

  // Detect whether the user has scrolled up. When they have, we STOP
  // yanking the pane back to the bottom on background polls — this is
  // the fix for the "page keeps scrolling down" mobile bug.
  const onScroll = () => {
    const el = scrollBox.current;
    if (!el) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldStickBottom.current = gap < 80;
  };

  const loadContacts = async () => {
    // iter99c · trust the backend's scoping. `/messages/contacts` already
    // returns only the patient's assigned clinician (with a roster fallback
    // for unassigned patients). The old client-side name whitelist would
    // silently drop any clinician whose display name didn't match the four
    // hard-coded strings — leaving the patient with an empty chat pane
    // and no way to reach the person actually caring for them.
    const r = await api.get("/messages/contacts");
    const list = Array.isArray(r?.data) ? r.data : [];
    setContacts(list);
    setContactsLoading(false);
    return list;
  };

  const loadThread = async (contactId, opts = {}) => {
    if (!contactId) return;
    let r;
    try {
      r = await api.get(`/messages/thread/${contactId}`);
    } catch (e) {
      // Never crash the whole pane on a transient network error — the poller
      // will retry every 10 s. Just log and bail.
      console.warn("loadThread failed", e?.response?.status || e?.message);
      return;
    }
    // iter99b · The server always returns the full thread now. Filtering by
    // an "End chat" cutoff happens purely on the client so a stale server
    // cutoff can NEVER hide new clinician replies from the patient.
    const all = Array.isArray(r?.data) ? r.data : [];
    // The X-Thread-Cutoff header is still exposed so the "Chat history"
    // button knows a prior archive exists — but we do NOT hide messages
    // based on it. Only an in-session `endedAt` set by the user's own
    // "End chat" tap trims the pane, and even then, messages newer than
    // the cutoff still stream in normally.
    const list = (endedAt && !opts.ignoreCutoff)
      ? all.filter((m) => m.created_at > endedAt)
      : all;
    // Only jump-to-bottom when (a) this is the FIRST render for the pane,
    // (b) the user is already near the bottom (sticky), or (c) opts.force.
    const grew = list.length > lastCount.current;
    const firstLoad = opts.firstLoad === true;
    setMessages(list);
    lastCount.current = list.length;
    if (opts.ignoreCutoff) setHistoryLoaded(true);
    if (firstLoad || opts.force || (grew && shouldStickBottom.current)) {
      setTimeout(() => scrollBoxToBottom(scrollBox.current, !firstLoad), 60);
    }
  };

  useEffect(() => {
    (async () => {
      const list = await loadContacts();
      if (list.length && !active) {
        // If the page was opened with ?to=<contactId>, land on that contact.
        const pref = preselectContactId && list.find((c) => c.id === preselectContactId);
        setActive(pref || list[0]);
      }
    })();
    if (isPatient) {
      api.get("/patient/gp-info").then((r) => setGp(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  // One-shot prefill of the composer when arriving from another page (e.g. the
  // "Request a change" button on an appointment). Uses decodeURIComponent so
  // multi-line text survives round-tripping through the URL.
  useEffect(() => {
    if (prefillBody) {
      try { setText(decodeURIComponent(prefillBody)); }
      catch { setText(prefillBody); }
    }
    // eslint-disable-next-line
  }, [prefillBody]);

  const openContact = (c) => {
    // Switching contacts resets the "ended" state — new context, fresh view.
    setEndedAt(null);
    setHistoryLoaded(false);
    setActive(c);
    setUnread((u) => ({ ...u, [c.id]: 0 })); // optimistic — opening marks read
  };

  // Keep per-conversation unread counts fresh (sidebar badges + unread-first order).
  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { if (active) { lastCount.current = 0; shouldStickBottom.current = true; loadThread(active.id, { firstLoad: true }).then(loadUnread).catch(() => {}); } /* eslint-disable-next-line */ }, [active?.id]);

  // Auto-poll every 10 s so incoming messages surface without a manual refresh.
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => { loadThread(active.id).catch(() => {}); }, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [active?.id, endedAt]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const list = await loadContacts();
      if (active && !list.some((c) => c.id === active.id)) {
        setActive(list[0] || null);
      } else if (active) {
        await loadThread(active.id);
      }
      toast.success("Chat refreshed");
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const endChat = async () => {
    if (!active) return;
    // iter99 · persist the cutoff on the server so archive survives reloads.
    try {
      const { data } = await api.post(`/messages/thread/${active.id}/end`);
      setEndedAt(data?.ended_at || new Date().toISOString());
    } catch {
      // Non-blocking — fall back to local-only cutoff so the UX still archives
      // in the current session even if the server call failed.
      setEndedAt(new Date().toISOString());
    }
    setMessages([]);
    setHistoryLoaded(false);
    setText("");
    toast.success(`Chat archived with ${active.name?.split(" ")[0] || "this contact"}. Say hi to start a new thread.`);
  };

  const showHistory = async () => {
    if (!active) return;
    setRefreshing(true);
    try { await loadThread(active.id, { ignoreCutoff: true }); }
    finally { setRefreshing(false); }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const body = text.trim();
    setText("");
    const optimistic = { id: `tmp-${Date.now()}`, sender_id: user.id, body, created_at: new Date().toISOString() };
    setMessages((s) => [...s, optimistic]);
    setTimeout(() => scrollBoxToBottom(scrollBox.current, true), 60);
    try {
      await api.post("/messages", { recipient_id: active.id, body });
      await loadThread(active.id, { force: true });
    } catch { toast.error("Message failed."); }
  };

  const saveGp = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/patient/gp-info", gp);
      setGp(data);
      setGpEdit(false);
      toast.success("GP details saved");
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  const openNhsApp = () => {
    // Deep-link to the NHS App on mobile, web fallback for desktop
    const web = "https://www.nhsapp.service.nhs.uk/";
    const mobile = "nhsapp://open";
    const t = Date.now();
    window.location.href = mobile;
    setTimeout(() => {
      // if still on the same page after a short delay, open web
      if (Date.now() - t < 1500) window.open(web, "_blank", "noopener,noreferrer");
    }, 800);
  };

  return (
    <DashLayout>
      <div className="mb-6">
        <div className="fl-label text-[#f47a20]">{t("pages.messages.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.messages.h1")}</h1>
        <p className="text-sm text-zinc-500 mt-2">Not an emergency service. For urgent symptoms call your local emergency number.</p>
      </div>

      <div className="grid md:grid-cols-4 gap-4 fl-card overflow-hidden mb-6" style={{ minHeight: 520 }}>
        <div className="border-r border-white/10 md:col-span-1 max-h-[520px] overflow-auto">
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <div className="fl-label">{isPatient ? "Clinicians" : "Patients"}</div>
            <button
              onClick={refreshAll}
              className="text-zinc-400 hover:text-[#f47a20]"
              title="Refresh contacts and messages"
              data-testid="msg-refresh-contacts"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
          {contactsLoading && contacts.length === 0 ? (
            <div className="p-3 space-y-2" data-testid="messages-contacts-loading">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-2">
                  <Skeleton className="h-3 w-32 rounded-md" />
                  <Skeleton className="h-2 w-20 mt-2 rounded" />
                </div>
              ))}
            </div>
          ) : [...contacts].sort((a, b) => ((unread[b.id] || 0) > 0 ? 1 : 0) - ((unread[a.id] || 0) > 0 ? 1 : 0)).map((c) => {
            const n = unread[c.id] || 0;
            return (
            <button key={c.id} onClick={() => openContact(c)}
              className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/5 transition-colors flex items-center justify-between gap-2 ${active?.id === c.id ? "bg-white/5 border-l-2 border-l-[#f47a20]" : ""} ${n > 0 ? "bg-[color:var(--fl-orange)]/5" : ""}`}
              data-testid={`contact-${c.id}`}>
              <div className="min-w-0">
                <div className={`fl-heading text-sm truncate ${n > 0 ? "text-[color:var(--fl-orange)]" : ""}`}>{c.name}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{c.role}</div>
              </div>
              {n > 0 && (
                <span className="shrink-0 min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full text-[11px] font-bold text-black" style={{ background: "var(--fl-orange)" }} data-testid={`contact-unread-${c.id}`}>{n}</span>
              )}
            </button>
          );})}
          {!contactsLoading && contacts.length === 0 && <div className="p-4 text-xs text-zinc-500">No contacts.</div>}
        </div>
        <div className="md:col-span-3 flex flex-col max-h-[520px]">
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="fl-heading text-lg truncate">{active?.name || "Select a contact"}</div>
              <div className="fl-label">{active?.role}</div>
              {/* Clinician-side: show quick patient basics right under the header */}
              {!isPatient && active?.role === "patient" && (
                <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px]" style={{ color: "var(--fl-muted)" }} data-testid="chat-patient-basics">
                  {active.email && <span>{active.email}</span>}
                  {active.phone && <span>· {active.phone}</span>}
                  {active.dob && <span>· DOB {new Date(active.dob).toLocaleDateString("en-GB")}</span>}
                  {active.sport && <span>· {active.sport}</span>}
                  {active.main_goal && <span>· Goal: {active.main_goal}</span>}
                </div>
              )}
            </div>
            {active && (
              <button
                onClick={refreshAll}
                className="fl-btn-ghost text-xs"
                title="Reload messages"
                data-testid="msg-refresh"
              >
                <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
              </button>
            )}
            {active && endedAt && !historyLoaded && (
              <button
                onClick={showHistory}
                className="fl-btn-ghost text-xs"
                title="Reveal messages sent before the chat was ended"
                data-testid="msg-show-history"
              >
                <History size={13} /> Chat history
              </button>
            )}
            {active && messages.length > 0 && !endedAt && (
              <button
                onClick={endChat}
                className="fl-btn-ghost text-xs"
                title="Clear the visible chat — history stays saved"
                data-testid="msg-end-chat"
              >
                <MessageSquareOff size={13} /> End chat
              </button>
            )}
          </div>
          <div ref={scrollBox} onScroll={onScroll} className="flex-1 overflow-auto p-4 space-y-3" style={{ overscrollBehavior: "contain" }}>
            {messages.length === 0 && !endedAt && (
              <div className="h-full flex items-center justify-center text-center text-sm py-8" style={{ color: "var(--fl-muted-2)" }}>
                No messages yet with {active?.name?.split(" ")[0] || "this contact"}. Say hi to start the conversation.
              </div>
            )}
            {messages.length === 0 && endedAt && (
              <div className="h-full flex items-center justify-center text-center text-sm py-8" style={{ color: "var(--fl-muted-2)" }}>
                Chat cleared with <b>{active?.name?.split(" ")[0] || "this contact"}</b>. Send a new message to start a fresh conversation,<br />
                or tap <b>Chat history</b> to review previous messages.
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === user.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] p-3 rounded-md ${mine ? "bg-[#f47a20] text-white" : "bg-[#1C1C1C] border border-white/10"}`}>
                    {m.video_id && (
                      <MediaVideo
                        id={m.video_id}
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full rounded mb-2 max-h-72 bg-black"
                        data-testid={`msg-video-${m.id}`}
                      />
                    )}
                    <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                    <div className="text-[10px] opacity-70 mt-1">{new Date(m.created_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottom} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2 items-end">
            <textarea
              className="fl-input min-h-[42px] max-h-32 resize-none"
              rows={1}
              placeholder="Write a message..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              data-testid="msg-input"
            />
            <button className="fl-btn" onClick={send} data-testid="msg-send"><Send size={14} /></button>
          </div>
        </div>
      </div>

      {isPatient && (
        <SortableGrid
          items={panelOrder}
          onReorder={setPanelOrder}
          onResize={sizePanel}
          sizes={panelSizes}
          editMode={editLayout}
          className="grid md:grid-cols-2 gap-4"
        >
          {/* GP contact card */}
          <SortableGrid.Item key="gp" id="gp">
          <div className="fl-card fl-card-elevated p-6" data-testid="gp-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Stethoscope size={18} className="text-[#f47a20]" />
                <div className="fl-heading text-lg">Your GP</div>
              </div>
              {!gpEdit ? (
                <button className="fl-btn-ghost text-xs" onClick={() => setGpEdit(true)} data-testid="gp-edit-btn">Edit</button>
              ) : (
                <button className="fl-btn text-xs" onClick={saveGp} disabled={saving} data-testid="gp-save-btn">
                  <Save size={12} /> {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
            {gpEdit ? (
              <div className="space-y-3">
                {[
                  ["gp_name", "GP name", "Dr Jane Smith"],
                  ["gp_surgery", "Surgery", "Ashfield Medical Centre"],
                  ["gp_phone", "Phone", "020 7946 0999"],
                  ["gp_email", "Email", "reception@surgery.nhs.uk"],
                  ["nhs_number", "NHS number", "485 777 3456"],
                ].map(([k, l, ph]) => (
                  <div key={k}>
                    <div className="fl-label mb-1">{l}</div>
                    <input className="fl-input" value={gp[k] || ""} placeholder={ph}
                      onChange={(e) => setGp({ ...gp, [k]: e.target.value })} data-testid={`gp-input-${k}`} />
                  </div>
                ))}
              </div>
            ) : gp.gp_name || gp.gp_phone || gp.gp_email || gp.nhs_number ? (
              <div className="space-y-2 text-sm">
                {gp.gp_name && <div><span className="fl-label mr-2">Name</span>{gp.gp_name}</div>}
                {gp.gp_surgery && <div><span className="fl-label mr-2">Surgery</span>{gp.gp_surgery}</div>}
                {gp.nhs_number && <div><span className="fl-label mr-2">NHS #</span><span className="font-mono">{gp.nhs_number}</span></div>}
                <div className="flex gap-2 mt-3 flex-wrap">
                  {gp.gp_phone && (
                    <a href={`tel:${gp.gp_phone.replace(/\s/g, "")}`} className="fl-btn text-sm" data-testid="gp-call-btn">
                      <Phone size={14} /> Call {gp.gp_phone}
                    </a>
                  )}
                  {gp.gp_email && (
                    <a href={`mailto:${gp.gp_email}`} className="fl-btn-ghost text-sm" data-testid="gp-email-btn">
                      <Mail size={14} /> Email
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-zinc-500">Add your GP so we can share reports and you can reach them in one tap.</div>
            )}
          </div>
          </SortableGrid.Item>

          {/* NHS quick actions */}
          <SortableGrid.Item key="nhs" id="nhs">
          <div className="fl-card fl-card-elevated p-6" data-testid="nhs-panel">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={18} className="text-[#005EB8]" />
              <div className="fl-heading text-lg">NHS quick actions</div>
            </div>
            <p className="text-xs text-zinc-500 mb-4">The NHS App gives you access to your health record, GP services and prescriptions.</p>
            <div className="grid grid-cols-1 gap-2">
              <button onClick={openNhsApp} className="fl-btn justify-start" data-testid="nhs-app-btn">
                <ExternalLink size={14} /> Open NHS App
              </button>
              <a href="tel:111" className="fl-btn-ghost justify-start" data-testid="nhs-111-btn">
                <PhoneCall size={14} /> Call NHS 111 (non-emergency)
              </a>
              <a
                href="https://www.nhs.uk/service-search/find-a-gp"
                target="_blank"
                rel="noreferrer"
                className="fl-btn-ghost justify-start"
                data-testid="nhs-find-gp-btn"
              >
                <MapPin size={14} /> Find a GP near you
              </a>
            </div>
            <div className="text-[11px] text-zinc-500 mt-4">In a medical emergency call 999.</div>
          </div>
          </SortableGrid.Item>
        </SortableGrid>
      )}
    </DashLayout>
  );
}
